/**
 * AffiliateProfilePage — a deliberately simple public profile: who they are,
 * who leads, where they are and a short description. No members, services or
 * finance sub-modules; that belongs to the FWM network churches only.
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, MapPin, UserRound, Mail, Phone, Globe, CalendarDays, Info,
} from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY } from '@/components/fwm/imagery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3 py-3.5 border-b border-[color:var(--fwm-line)] last:border-0"
       data-testid={testId}>
    <Icon className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--fwm-muted)]" />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--fwm-muted)]">
        {label}
      </p>
      <p className="mt-1 text-sm break-words">{children}</p>
    </div>
  </div>
);

export default function AffiliateProfilePage() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['affiliate-profile', slug],
    queryFn: () => fetch(`${API_URL}/api/affiliates/profile/${slug}`).then((r) => {
      if (!r.ok) throw new Error('not found');
      return r.json();
    }),
    retry: false,
  });

  const a = data?.affiliate;
  const hero = useHero('affiliate_profile', {
    image_url: FWM_IMAGERY.congregation || FWM_IMAGERY.choir,
  });

  return (
    <FwmShell ctaLabel="Affiliate directory" ctaLink="/affiliated-ministries"
              testId="affiliate-profile-page" overDark={Boolean(a)}>
      {a && (
        <FwmHero
          testId="affiliate-profile-hero"
          image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
          video={hero.video_url}
          mediaPriority={hero.media_priority}
          eyebrow={`Affiliated ${a.org_type === 'ministry' ? 'ministry' : 'church'}`}
          title={a.org_name}
          blurb={[a.city, a.country].filter(Boolean).join(', ')}
        />
      )}
      <section className="mx-auto max-w-[900px] px-6 lg:px-10 pt-14 pb-24">
        <Link to="/affiliated-ministries"
              className="inline-flex items-center gap-2 text-xs font-semibold text-[color:var(--fwm-accent-dark)]"
              data-testid="affiliate-profile-back">
          <ArrowLeft className="h-3.5 w-3.5" /> All affiliated churches &amp; ministries
        </Link>

        {isLoading ? (
          <Skeleton className="h-72 w-full mt-8" />
        ) : isError || !a ? (
          <div className="fwm-card mt-8 p-12 text-center" data-testid="affiliate-profile-missing">
            <h1 className="fwm-display text-2xl">This affiliate is not currently listed</h1>
            <p className="mt-3 text-sm text-[color:var(--fwm-muted)]">
              Membership may have lapsed, or the link is out of date.
            </p>
          </div>
        ) : (
          <>
            {a.description && (
              <p className="mt-8 text-base leading-relaxed text-[color:var(--fwm-muted)]"
                 data-testid="affiliate-profile-description">
                {a.description}
              </p>
            )}
            <span className="hidden" data-testid="affiliate-profile-name">{a.org_name}</span>

            <div className="fwm-card mt-10 px-7 py-4">
              <Row icon={UserRound} label="Leadership" testId="affiliate-profile-pastor">
                {[a.pastor_title, a.pastor_name].filter(Boolean).join(' ') || '—'}
              </Row>
              <Row icon={MapPin} label="Location" testId="affiliate-profile-location">
                {[a.city, a.country].filter(Boolean).join(', ') || '—'}
              </Row>
              {a.years_in_ministry != null && (
                <Row icon={CalendarDays} label="Years in ministry">{a.years_in_ministry}</Row>
              )}
              {a.affiliated_since && (
                <Row icon={CalendarDays} label="Affiliated since">
                  {new Date(a.affiliated_since).toLocaleDateString(undefined,
                    { month: 'long', year: 'numeric' })}
                </Row>
              )}
              {a.contact_email && (
                <Row icon={Mail} label="Email" testId="affiliate-profile-email">
                  <a href={`mailto:${a.contact_email}`}
                     className="text-[color:var(--fwm-accent-dark)]">{a.contact_email}</a>
                </Row>
              )}
              {a.contact_phone && (
                <Row icon={Phone} label="Phone">{a.contact_phone}</Row>
              )}
              {a.website && (
                <Row icon={Globe} label="Website">
                  <a href={a.website} target="_blank" rel="noreferrer"
                     className="text-[color:var(--fwm-accent-dark)]">{a.website}</a>
                </Row>
              )}
            </div>

            <div className="mt-8 flex items-start gap-3 text-xs leading-relaxed text-[color:var(--fwm-muted)]"
                 data-testid="affiliate-profile-note">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <p>
                {a.org_name} is an independent work under the covering of the Archbishop and is not
                part of the Faith World Ministries church network.
              </p>
            </div>
          </>
        )}
      </section>
    </FwmShell>
  );
}
